import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ReactComponent as CloseCircleIcon } from '@assets/icons/close-circle.svg';
import { ReactComponent as SuccessIcon } from '@assets/icons/Success.svg';

import CommonImage from '@components/CommonImage';
import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import { getPublishStatus } from '@features/design/utils/designUtils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getDesignPublishStatus, submitPublishDesign } from '@services/collectionService';

import { getProductStatusWiseColor, printApiError } from '@utils/Util';

const PublishDesign = ({ onClose, selectedProducts = [], onSubmitSuccess }) => {
	const [productList, setProductList] = useState([]);
	const [publishableProductsIds, setPublishableProductsIds] = useState([]);
	const dispatch = useDispatch();

	const handlePublish = async () => {
		let response = null;
		try {
			dispatch(openLoader());
			response = await submitPublishDesign(publishableProductsIds?.join(','));
			onSubmitSuccess && onSubmitSuccess(publishableProductsIds);
		} catch (error) {
			printApiError(error);
		} finally {
			const unpublishableCount = productList?.length - publishableProductsIds?.length;
			if (unpublishableCount > 0) {
				toastSuccess(
					`${publishableProductsIds?.length}/${productList?.length} Styles published successfully.`
				);
			} else {
				toastSuccess(response?.message || 'Published successfully!');
			}
			dispatch(closeLoader());
			onClose();
		}
	};

	const getPublishableProducts = (products) => {
		return products
			.filter(
				(item) =>
					item?.hasSample &&
					item?.hasBodyFabric &&
					item?.hasMeasurement &&
					item?.hasPresentation &&
					item?.hasBasePrice
			)
			.map((item) => item?.productResponse?.id);
	};

	const shouldIncludeProduct = (product) => {
		return (
			product?.productResponse?.availabilityStatus !== 'PUBLISHED' &&
			product?.productResponse?.setPackStyleFlag !== 'SET_STYLE'
		);
	};

	const fetchProductsList = async () => {
		try {
			const data = await getDesignPublishStatus(selectedProducts?.join(','));
			const excludePublishedProduct = data?.filter(shouldIncludeProduct);
			setProductList(excludePublishedProduct);
			setPublishableProductsIds(getPublishableProducts(excludePublishedProduct));
		} catch (error) {
			printApiError(error);
		}
	};

	useEffect(() => {
		fetchProductsList();
	}, [selectedProducts?.length]);

	const renderStatusList = (_product, index) => {
		return getPublishStatus([productList[index]])?.map((item) => (
			<li key={item?.name}>
				<div className="d-flex align-items-center">
					{item?.successIcon ? <SuccessIcon /> : <CloseCircleIcon />}
					<p className="ml-1 gray_dark_02 regular-12">{item?.name}</p>
				</div>
			</li>
		));
	};

	const renderProductRow = (product, index) => (
		<tr key={product?.productResponse?.id}>
			<td className="design-column">
				<div className="d-flex align-items-center">
					<div className="image">
						<CommonImage
							src={product?.productResponse?.designDocuments[0]?.docUrl}
							alt="image"
							isStatic={false}
						/>
					</div>
					<div className="design-content ml-12">
						<p className="mb-1 gray_dark_01 regular-12">
							{product?.productResponse?.referenceNumber}
						</p>
						<ul className="common-list-component">
							<li>{product?.productResponse?.productGroup}</li>
							<li>{product?.productResponse?.subCategory}</li>
						</ul>
					</div>
				</div>
			</td>
			<td className="status-column">
				<div className="status">
					{product?.productResponse?.productStatusList?.map((item) => (
						<span
							className={`status ${getProductStatusWiseColor[item?.status] || 'purple-1'} mr-1`}
							key={item?.status}
						>
							{item?.status}
						</span>
					))}
				</div>
				<div className="status-list mt-2">
					<ul>{renderStatusList(product, index)}</ul>
				</div>
			</td>
		</tr>
	);

	return (
		<ModalContainer onClose={onClose} className="publish-design-modal">
			<div className="common-popup shadow-24dp">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">Publish Design</h3>
					</div>
					<div className="close-btn">
						<CommonImage src={`/icons/close.svg`} alt="close" onClick={onClose} />
					</div>
				</div>
				<div className="common-popup-body scroll-y-label">
					<div className="publish-design-table">
						<table className="table with-border">
							<thead>
								<tr>
									<td>Designs({productList.length})</td>
									<td>Status</td>
								</tr>
							</thead>
							<tbody>{productList?.map(renderProductRow)}</tbody>
						</table>
					</div>
				</div>
				<div className="modal-footer text-right">
					<button
						className="button size32 ml-2"
						disabled={publishableProductsIds?.length === 0}
						onClick={handlePublish}
					>
						Publish ({publishableProductsIds?.length} Styles)
					</button>
				</div>
			</div>
		</ModalContainer>
	);
};

export default PublishDesign;
